import type {
  GetPageDataResponse,
  CheckEnrollmentEligibilityRequest,
  CheckEnrollmentEligibilityResponse,
  EnrollTaskRequest,
  EnrollTaskResponse,
  BatchEnrollTasksRequest,
  BatchEnrollTasksResponse,
  RecordProgressRequest,
  RecordProgressResponse,
  BatchRecordProgressRequest,
  BatchRecordProgressResponse,
  GetProgressResponse,
  GetJournalResponse,
  StockQuotesResponse,
} from '@ark/material-checkin-shared';
import { activityConfig } from '@ark/material-checkin-shared';
import { BaseApiClient } from '@ark/web-api/frontend';

export class MaterialCheckinApiClient extends BaseApiClient {
  constructor(private readonly actPlaybookId: string) {
    super({
      apiPrefix: activityConfig.apiPrefix,
    });
  }

  /**
   * 获取页面数据
   *
   * 返回活动配置，前端可以根据 isActive 判断是否可以报名
   */
  async getPageData(): Promise<GetPageDataResponse> {
    return await this.get<GetPageDataResponse>('page-data', {
      actPlaybookId: this.actPlaybookId,
    });
  }

  async checkEligibility(
    taskId: string,
  ): Promise<CheckEnrollmentEligibilityResponse> {
    const request: CheckEnrollmentEligibilityRequest = {
      actPlaybookId: this.actPlaybookId,
      taskId,
    };
    return await this.post<CheckEnrollmentEligibilityResponse>(
      'check-eligibility',
      request,
    );
  }

  async enrollTask(taskId: string): Promise<EnrollTaskResponse> {
    const request: EnrollTaskRequest = {
      actPlaybookId: this.actPlaybookId,
      taskId,
    };
    return await this.post<EnrollTaskResponse>('enroll', request);
  }

  /**
   * 批量报名任务
   * 一次请求报名多个任务，减少网络请求次数
   */
  async batchEnrollTasks(taskIds: string[]): Promise<BatchEnrollTasksResponse> {
    const request: BatchEnrollTasksRequest = {
      actPlaybookId: this.actPlaybookId,
      taskIds,
    };
    return await this.post<BatchEnrollTasksResponse>('batch-enroll', request);
  }

  async recordProgress(
    taskId: string,
    materialId: string,
  ): Promise<RecordProgressResponse> {
    const request: RecordProgressRequest = {
      actPlaybookId: this.actPlaybookId,
      taskId,
      materialId,
    };
    return await this.post<RecordProgressResponse>('record-progress', request);
  }

  /**
   * 批量上报进度
   * 一次请求上报多个任务的进度，BFF 层并发调用，减少前端网络请求次数
   */
  async batchRecordProgress(
    taskIds: string[],
    materialId: string,
  ): Promise<BatchRecordProgressResponse> {
    const request: BatchRecordProgressRequest = {
      actPlaybookId: this.actPlaybookId,
      taskIds,
      materialId,
    };
    return await this.post<BatchRecordProgressResponse>(
      'batch-record-progress',
      request,
    );
  }

  async getProgress(taskId: string): Promise<GetProgressResponse> {
    return await this.get<GetProgressResponse>('progress', {
      actPlaybookId: this.actPlaybookId,
      taskId,
    });
  }

  async getJournal(
    taskId: string,
    pageSize = 50,
    startTime?: number,
  ): Promise<GetJournalResponse> {
    const params: Record<string, unknown> = { taskId, pageSize };
    if (startTime) {
      params.startTime = startTime;
    }
    return await this.get<GetJournalResponse>('journal', params);
  }

  async getStockQuotes(stockIds: string[]): Promise<StockQuotesResponse> {
    return await this.post<StockQuotesResponse>('stock-quotes', { stockIds });
  }
}
